import { Component, Input } from '@angular/core';

export interface TrendDataPoint {
  period: string;
  count: number;
}

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'] as const;
const DOW = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const;

@Component({
  selector: 'app-sentiment-details-trend-widget',
  standalone: false,
  templateUrl: './details-trend-widget.component.html',
  styleUrls: ['./details-trend-widget.component.scss'],
})
export class SentimentDetailsTrendWidgetComponent {
  @Input() trendData: TrendDataPoint[] = [];
  @Input() loading = false;
  /** Matches Details trend dropdown: `daily` | `weekly` | `monthly` | `quarterly` | `yearly`. */
  @Input() timePeriod = '';

  get maxCount(): number {
    if (!this.trendData?.length) return 1;
    return Math.max(...this.trendData.map((d) => d.count), 1);
  }

  /**
   * Trend x-axis labels by granularity (local calendar date from `YYYY-MM-DD` prefix when present).
   * Weekly uses series position: Week 1, Week 2, …
   */
  displayPeriod(period: string, seriesIndex = 0): string {
    const d = this.parseBucketDate(period);
    if (!d) return this.fallbackPeriodLabel(period);

    const gran = String(this.timePeriod ?? '').trim().toLowerCase();
    switch (gran) {
      case 'daily':
        return this.formatDaily(d);
      case 'weekly':
        return this.formatWeekly(d, seriesIndex);
      case 'monthly':
        return this.formatMonthly(d);
      case 'quarterly':
        return this.formatQuarterly(d);
      case 'yearly':
        return String(d.getFullYear());
      default:
        return this.fallbackPeriodLabel(period);
    }
  }

  private parseBucketDate(period: string): Date | null {
    const raw = String(period ?? '').trim();
    if (!raw) return null;
    const head = raw.replace('T', ' ').split(/\s+/)[0] ?? '';
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(head);
    if (m) {
      const y = Number(m[1]);
      const mo = Number(m[2]) - 1;
      const da = Number(m[3]);
      const d = new Date(y, mo, da);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private pad2(n: number): string {
    return String(n).padStart(2, '0');
  }

  private formatDaily(d: Date): string {
    const y = d.getFullYear();
    const m = this.pad2(d.getMonth() + 1);
    const day = this.pad2(d.getDate());
    return `${y}-${m}-${day}(${DOW[d.getDay()]})`;
  }

  private formatWeekly(d: Date, seriesIndex: number): string {
    const y = d.getFullYear();
    const m = this.pad2(d.getMonth() + 1);
    const day = this.pad2(d.getDate());
    return `${y}-${m}-${day}(Week ${seriesIndex + 1})`;
  }

  private formatMonthly(d: Date): string {
    const y = d.getFullYear();
    const m = this.pad2(d.getMonth() + 1);
    const day = this.pad2(d.getDate());
    return `${y}-${m}-${day}(${MONTHS[d.getMonth()]})`;
  }

  private formatQuarterly(d: Date): string {
    const q = Math.floor(d.getMonth() / 3) + 1;
    const y = d.getFullYear();
    const m = this.pad2(d.getMonth() + 1);
    const day = this.pad2(d.getDate());
    return `${y}-${m}-${day}(Q${q})`;
  }

  /** Strip midnight noise for non-structured period strings. */
  private fallbackPeriodLabel(period: string): string {
    const raw = String(period ?? '').trim();
    if (!raw) return '';

    const midnight =
      /^(\d{4}-\d{2}-\d{2})[T ](?:00:00(?::00)?(?:\.\d+)?)(?:Z|[+-][\d:]+)?$/i.exec(raw);
    if (midnight) {
      return midnight[1];
    }

    let s = raw.replace('T', ' ').replace(/Z$/i, '').trim();
    s = s.replace(/\s00:00(?::00)?(?:\.\d+)?$/i, '').trim();
    return s.length > 16 ? s.slice(0, 16) : s;
  }
}
